// src/components/auth/Signup.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../layout/Header';
import Navigation from '../layout/Navigation';

/* ─── Inline keyframe styles injected once ─── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

  :root {
    --ink:    #1c1d2e;
    --slate:  #41425A;
    --mid:    #5a5b75;
    --mist:   #DBDCED;
    --accent: #6c63ff;
    --green:  #22c55e;
    --red:    #ef4444;
  }

  .sgn-root * { font-family: 'DM Sans', sans-serif; }
  .sgn-display { font-family: 'Sora', sans-serif; }

  /* ---------- background orbs ---------- */
  @keyframes orb-drift {
    0%,100% { transform: translate(0,0) scale(1); }
    33%      { transform: translate(40px,-30px) scale(1.08); }
    66%      { transform: translate(-20px,20px) scale(0.94); }
  }
  .orb { position:absolute; border-radius:50%; filter:blur(70px); opacity:.35; animation: orb-drift 14s ease-in-out infinite; pointer-events:none; }
  .orb-1 { width:420px;height:420px;background:radial-gradient(circle,#6c63ff,transparent 70%); top:-120px;left:-100px; animation-delay:0s; }
  .orb-2 { width:320px;height:320px;background:radial-gradient(circle,#a78bfa,transparent 70%); bottom:-80px;right:-60px; animation-delay:-5s; }
  .orb-3 { width:240px;height:240px;background:radial-gradient(circle,#38bdf8,transparent 70%); top:50%;left:60%; animation-delay:-9s; }

  .orb-dk-1 { background:radial-gradient(circle,#4338ca,transparent 70%); opacity:.25; }
  .orb-dk-2 { background:radial-gradient(circle,#7c3aed,transparent 70%); opacity:.2; }
  .orb-dk-3 { background:radial-gradient(circle,#0ea5e9,transparent 70%); opacity:.15; }

  /* ---------- card slide-in ---------- */
  @keyframes card-in {
    from { opacity:0; transform: translateY(28px) scale(.97); }
    to   { opacity:1; transform: translateY(0)    scale(1);   }
  }
  .card-animate { animation: card-in .55s cubic-bezier(.22,.68,0,1.2) both; }

  /* ---------- step content fade ---------- */
  @keyframes step-in {
    from { opacity:0; transform:translateX(18px); }
    to   { opacity:1; transform:translateX(0);    }
  }
  .step-animate { animation: step-in .35s ease both; }

  /* ---------- floating label input ---------- */
  .fl-wrap { position:relative; }
  .fl-wrap input, .fl-wrap select {
    padding: 22px 14px 8px;
    font-size: 15px;
    line-height: 1.4;
    width:100%;
    border-radius:14px;
    border:2px solid transparent;
    background-clip: padding-box;
    outline:none;
    transition: border-color .2s, box-shadow .2s, background .2s;
  }
  .fl-wrap label {
    position:absolute;
    left:15px; top:50%;
    transform:translateY(-50%);
    font-size:14px;
    pointer-events:none;
    transition: top .2s, font-size .2s, color .2s;
    font-family:'DM Sans',sans-serif;
  }
  .fl-wrap input:focus + label,
  .fl-wrap input:not(:placeholder-shown) + label,
  .fl-wrap select:focus + label,
  .fl-wrap.has-val label {
    top:10px; font-size:11px; font-weight:600; letter-spacing:.05em; text-transform:uppercase;
  }
  .fl-wrap input:focus, .fl-wrap select:focus { border-color: var(--accent); box-shadow: 0 0 0 4px rgba(108,99,255,.15); }

  /* light theme inputs */
  .fl-light input, .fl-light select { background:#f5f5fb; border-color:#e0e0ee; color:var(--ink); }
  .fl-light label { color:#9090b0; }
  .fl-light input:focus + label, .fl-light input:not(:placeholder-shown) + label,
  .fl-light select:focus + label, .fl-light.has-val label { color:var(--accent); }

  /* dark theme inputs */
  .fl-dark input, .fl-dark select { background:rgba(255,255,255,.06); border-color:rgba(255,255,255,.1); color:#e8e8f4; }
  .fl-dark label { color:#8888aa; }
  .fl-dark input:focus + label, .fl-dark input:not(:placeholder-shown) + label,
  .fl-dark select:focus + label, .fl-dark.has-val label { color:#a78bfa; }
  .fl-dark input::placeholder { color:transparent; }
  .fl-light input::placeholder { color:transparent; }

  /* ---------- progress pip ---------- */
  @keyframes pip-pop { 0%{transform:scale(1)} 50%{transform:scale(1.25)} 100%{transform:scale(1)} }
  .pip-active { animation: pip-pop .3s ease; }

  /* ---------- interest chip ---------- */
  .chip { transition: all .18s cubic-bezier(.34,1.56,.64,1); }
  .chip:hover { transform: scale(1.04); }
  .chip-on { transform: scale(1.04); }

  /* ---------- button shine ---------- */
  @keyframes shine {
    from { left:-120%; }
    to   { left: 120%; }
  }
  .btn-primary { position:relative; overflow:hidden; }
  .btn-primary::after {
    content:'';
    position:absolute;
    top:0; left:-120%;
    width:60%; height:100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,.25), transparent);
    transform:skewX(-20deg);
  }
  .btn-primary:hover::after { animation: shine .55s ease; }

  /* ---------- error shake ---------- */
  @keyframes shake { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-6px)} 40%{transform:translateX(6px)} 60%{transform:translateX(-4px)} 80%{transform:translateX(4px)} }
  .shake { animation: shake .4s ease; }

  /* ---------- decorative icon float ---------- */
  @keyframes icon-float { 0%,100%{transform:translateY(0) rotate(-3deg)} 50%{transform:translateY(-8px) rotate(3deg)} }
  .icon-float { animation: icon-float 3.5s ease-in-out infinite; }

  /* ---------- connector line ---------- */
  .step-connector { transition: background .4s ease; }

  /* scrollbar */
  .sgn-root ::-webkit-scrollbar { width:5px; }
  .sgn-root ::-webkit-scrollbar-track { background:transparent; }
  .sgn-root ::-webkit-scrollbar-thumb { background:#6c63ff55; border-radius:99px; }
`;

/* ─── helper: floating-label wrapper ─── */
const Field = ({ label, name, type = 'text', value, onChange, isDark, required, icon, as: As = 'input', children }) => {
  const themeClass = isDark ? 'fl-dark' : 'fl-light';
  const hasVal = value !== '' && value !== undefined;
  return (
    <div className={`fl-wrap ${themeClass} ${hasVal ? 'has-val' : ''}`}>
      {As === 'select' ? (
        <select id={name} name={name} value={value} onChange={onChange} required={required}>
          {children}
        </select>
      ) : (
        <input
          id={name} name={name} type={type} value={value}
          onChange={onChange} required={required} placeholder=" "
        />
      )}
      <label htmlFor={name}>
        {icon && <span className="mr-1">{icon}</span>}{label}{required ? ' *' : ''}
      </label>
    </div>
  );
};

/* ─── step metadata ─── */
const STEPS = [
  { n: 1, title: 'Login Details',         emoji: '🔐' },
  { n: 2, title: 'Personal Info',          emoji: '👤' },
  { n: 3, title: 'Agency & Preferences',   emoji: '🏛️' },
];

const INTERESTS = [
  { value: 'building-permits',    label: 'Building Permits',     emoji: '🏗️' },
  { value: 'zoning-regulations',  label: 'Zoning Regulations',   emoji: '📐' },
  { value: 'code-enforcement',    label: 'Code Enforcement',     emoji: '⚖️' },
  { value: 'planning-applications',label:'Planning Apps',        emoji: '📋' },
  { value: 'inspections',         label: 'Inspections',          emoji: '🔍' },
  { value: 'fire-safety',         label: 'Fire Safety',          emoji: '🚒' },
  { value: 'environmental-health',label: 'Env. Health',          emoji: '🌿' },
  { value: 'business-licensing',  label: 'Business Licensing',   emoji: '📄' },
  { value: 'public-works',        label: 'Public Works',         emoji: '🔧' },
  { value: 'property-assessment', label: 'Property Assessment',  emoji: '🏠' },
];

/* ═══════════════════════════════════════════ */
const Signup = () => {
  const navigate = useNavigate();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [errorMessage, setErrorMessage] = useState('');
  const [shakeError, setShakeError] = useState(false);
  const [stepKey, setStepKey] = useState(0); // force re-mount for animation
  const errorRef = useRef(null);

  const [formData, setFormData] = useState({
    username: '', email: '', password: '', confirmPassword: '',
    mobilePhone: '', agencyName: '', firstName: '', lastName: '',
    jurisdictionType: 'City', roleAtAgency: 'Accela Business Analyst',
    comments: '', emailUpdates: false, areasOfInterest: [],
  });

  useEffect(() => {
    const saved = localStorage.getItem('theme');
    const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
    setIsDarkMode(saved === 'dark' || (!saved && prefersDark));
  }, []);

  const toggleDarkMode = () => {
    setIsDarkMode(prev => {
      const next = !prev;
      localStorage.setItem('theme', next ? 'dark' : 'light');
      return next;
    });
  };

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleInterestToggle = val => {
    setFormData(prev => ({
      ...prev,
      areasOfInterest: prev.areasOfInterest.includes(val)
        ? prev.areasOfInterest.filter(i => i !== val)
        : [...prev.areasOfInterest, val],
    }));
  };

  const validateStep = step => {
    if (step === 1) return formData.username.trim() && formData.email.trim() && formData.password && formData.confirmPassword;
    if (step === 2) return formData.firstName.trim() && formData.lastName.trim() && formData.mobilePhone.trim();
    if (step === 3) return formData.agencyName.trim() !== '';
    return false;
  };

  const triggerError = msg => {
    setErrorMessage(msg);
    setShakeError(true);
    setTimeout(() => setShakeError(false), 420);
    errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  const handleNext = () => {
    if (!validateStep(currentStep)) { triggerError('Please fill in all required fields to proceed.'); return; }
    setErrorMessage('');
    setCurrentStep(s => s + 1);
    setStepKey(k => k + 1);
  };

  const handlePrevious = () => {
    setErrorMessage('');
    setCurrentStep(s => s - 1);
    setStepKey(k => k + 1);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validateStep(3)) { triggerError('Please complete Agency Details before submitting.'); return; }
    if (formData.password !== formData.confirmPassword) { triggerError("Passwords don't match."); return; }
    setErrorMessage('');
    try {
      const signupData = {
        ...formData,
        areasOfInterest: formData.areasOfInterest.length > 0
          ? formData.areasOfInterest
          : ['building-permits', 'zoning-regulations', 'code-enforcement'],
      };
      const res = await axios.post(
        process.env.REACT_APP_SIGNUP_URL || 'http://localhost:5000/api/signup',
        signupData
      );
      if (res.data.success) { navigate('/signin'); }
      else { triggerError(res.data.message || 'Signup failed.'); }
    } catch (err) {
      if (err.response?.status === 409) triggerError(err.response.data.message || 'That username or email is already taken.');
      else { console.error(err); triggerError('Signup failed. Please try again.'); }
    }
  };

  /* ── PROGRESS BAR ── */
  const renderProgress = () => (
    <div className="mb-10">
      <div className="flex items-center justify-center gap-0 mb-6">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.n}>
            {/* pip */}
            <div className="flex flex-col items-center">
              <div
                className={`pip-active w-11 h-11 rounded-full flex items-center justify-center font-bold text-base transition-all duration-400 shadow-lg
                  ${currentStep > s.n
                    ? 'bg-gradient-to-br from-green-400 to-green-600 text-white scale-95'
                    : currentStep === s.n
                      ? 'bg-gradient-to-br from-[#6c63ff] to-[#8b5cf6] text-white shadow-[0_0_20px_rgba(108,99,255,.5)]'
                      : isDarkMode
                        ? 'bg-gray-800 text-gray-500 border-2 border-gray-700'
                        : 'bg-gray-100 text-gray-400 border-2 border-gray-200'
                  }`}
                style={{ fontFamily: 'Sora, sans-serif' }}
              >
                {currentStep > s.n ? '✓' : s.n}
              </div>
              <span
                className={`mt-2 text-xs font-semibold tracking-wide whitespace-nowrap transition-colors duration-300
                  ${currentStep === s.n
                    ? (isDarkMode ? 'text-violet-300' : 'text-violet-600')
                    : (isDarkMode ? 'text-gray-500' : 'text-gray-400')
                  }`}
                style={{ fontFamily: 'Sora, sans-serif' }}
              >
                {s.title}
              </span>
            </div>
            {/* connector */}
            {i < STEPS.length - 1 && (
              <div
                className={`step-connector h-[3px] w-16 mx-1 mb-5 rounded-full transition-all duration-500
                  ${currentStep > s.n
                    ? 'bg-gradient-to-r from-green-400 to-violet-500'
                    : isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
                  }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* current step hero label */}
      <div className="text-center">
        <span
          className="text-4xl icon-float inline-block"
          aria-hidden="true"
        >
          {STEPS[currentStep - 1].emoji}
        </span>
        <h3
          className={`sgn-display text-xl font-700 mt-2 tracking-tight ${isDarkMode ? 'text-white' : 'text-gray-800'}`}
          style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
        >
          {STEPS[currentStep - 1].title}
        </h3>
      </div>
    </div>
  );

  /* ── STEP 1 ── */
  const renderLoginDetails = () => (
    <div className="step-animate space-y-5" key={stepKey}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Username" name="username" type="text" value={formData.username} onChange={handleChange} isDark={isDarkMode} required icon="👤" />
        <Field label="Email address" name="email" type="email" value={formData.email} onChange={handleChange} isDark={isDarkMode} required icon="📧" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Password" name="password" type="password" value={formData.password} onChange={handleChange} isDark={isDarkMode} required icon="🔒" />
        <Field label="Confirm Password" name="confirmPassword" type="password" value={formData.confirmPassword} onChange={handleChange} isDark={isDarkMode} required icon="🔒" />
      </div>

      {/* password strength hint */}
      {formData.password && (
        <div className="flex gap-1.5 items-center">
          {[1,2,3,4].map(i => (
            <div key={i} className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
              formData.password.length >= i * 3
                ? i <= 1 ? 'bg-red-400' : i === 2 ? 'bg-yellow-400' : i === 3 ? 'bg-blue-400' : 'bg-green-400'
                : isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
            }`} />
          ))}
          <span className={`text-xs ml-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {formData.password.length < 4 ? 'Weak' : formData.password.length < 8 ? 'Fair' : formData.password.length < 12 ? 'Good' : 'Strong'}
          </span>
        </div>
      )}

      {/* email updates */}
      <label
        className={`flex items-center gap-3 p-4 rounded-2xl cursor-pointer transition-all duration-200 border-2 ${
          formData.emailUpdates
            ? 'border-violet-400 bg-violet-50/50'
            : isDarkMode ? 'border-gray-700 bg-gray-800/30 hover:border-gray-600' : 'border-gray-200 bg-gray-50 hover:border-gray-300'
        }`}
      >
        <div
          className={`w-5 h-5 rounded-md flex items-center justify-center border-2 flex-shrink-0 transition-all duration-200 ${
            formData.emailUpdates ? 'bg-violet-500 border-violet-500' : isDarkMode ? 'border-gray-600' : 'border-gray-300'
          }`}
        >
          {formData.emailUpdates && <span className="text-white text-xs">✓</span>}
        </div>
        <input name="emailUpdates" type="checkbox" checked={formData.emailUpdates} onChange={handleChange} className="sr-only" />
        <div>
          <p className={`text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Email updates &amp; newsletters</p>
          <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Stay in the loop with platform news and tips</p>
        </div>
      </label>
    </div>
  );

  /* ── STEP 2 ── */
  const renderUserDetails = () => (
    <div className="step-animate space-y-5" key={stepKey}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="First Name" name="firstName" value={formData.firstName} onChange={handleChange} isDark={isDarkMode} required icon="✏️" />
        <Field label="Last Name"  name="lastName"  value={formData.lastName}  onChange={handleChange} isDark={isDarkMode} required icon="✏️" />
      </div>
      <Field label="Mobile Phone" name="mobilePhone" type="tel" value={formData.mobilePhone} onChange={handleChange} isDark={isDarkMode} required icon="📱" />

      {/* avatar / initials preview */}
      {(formData.firstName || formData.lastName) && (
        <div className={`flex items-center gap-4 p-4 rounded-2xl border-2 ${
          isDarkMode ? 'border-gray-700 bg-gray-800/30' : 'border-violet-100 bg-violet-50/40'
        }`}>
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center text-white text-xl font-bold shadow-lg"
            style={{ fontFamily: 'Sora, sans-serif' }}>
            {(formData.firstName[0] || '') + (formData.lastName[0] || '')}
          </div>
          <div>
            <p className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{formData.firstName} {formData.lastName}</p>
            <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Your profile preview</p>
          </div>
        </div>
      )}
    </div>
  );

  /* ── STEP 3 ── */
  const renderAgencyDetails = () => (
    <div className="step-animate space-y-5" key={stepKey}>
      <Field as="select" label="Agency Name" name="agencyName" value={formData.agencyName} onChange={handleChange} isDark={isDarkMode} required icon="🏛️">
        <option value="">Select agency…</option>
        <option value="GovTech">GovTech</option>
        <option value="Gwinnett">Gwinnett</option>
        <option value="COBB">COBB</option>
      </Field>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field as="select" label="Jurisdiction Type" name="jurisdictionType" value={formData.jurisdictionType} onChange={handleChange} isDark={isDarkMode} icon="🌐">
          <option>City</option><option>County</option><option>State</option>
        </Field>
        <Field as="select" label="Role at Agency" name="roleAtAgency" value={formData.roleAtAgency} onChange={handleChange} isDark={isDarkMode} icon="🎯">
          <option>IT Administrator</option>
          <option>Accela Developer</option>
          <option>Accela Business Analyst</option>
          <option>Accela Quality Analyst</option>
        </Field>
      </div>

      {/* Interest chips */}
      <div>
        <p className={`text-sm font-semibold mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`} style={{ fontFamily: 'Sora, sans-serif' }}>
          🎯 Areas of Interest <span className={`font-normal text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>(optional)</span>
        </p>
        <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          Pick topics to personalise your dashboard
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {INTERESTS.map(opt => {
            const on = formData.areasOfInterest.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => handleInterestToggle(opt.value)}
                className={`chip text-left px-3 py-2.5 rounded-xl border-2 text-xs font-medium flex items-center gap-2 transition-all
                  ${on
                    ? 'bg-violet-600/20 border-violet-500 text-violet-600 chip-on shadow-md'
                    : isDarkMode
                      ? 'bg-gray-800/60 border-gray-700 text-gray-400 hover:border-gray-500'
                      : 'bg-white border-gray-200 text-gray-600 hover:border-violet-300 hover:bg-violet-50'
                  }`}
              >
                <span className="text-base leading-none">{opt.emoji}</span>
                {opt.label}
                {on && <span className="ml-auto text-violet-500">✓</span>}
              </button>
            );
          })}
        </div>
        {formData.areasOfInterest.length > 0 && (
          <p className={`text-xs mt-2 ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`}>
            {formData.areasOfInterest.length} selected
          </p>
        )}
      </div>
    </div>
  );

  /* ── BUTTONS ── */
  const renderButtons = () => (
    <div className="flex justify-between items-center pt-8 mt-2 border-t border-dashed border-opacity-30"
      style={{ borderColor: isDarkMode ? 'rgba(255,255,255,.1)' : 'rgba(0,0,0,.1)' }}>
      {currentStep > 1 ? (
        <button
          type="button"
          onClick={handlePrevious}
          className={`flex items-center gap-2 py-3 px-6 rounded-2xl font-semibold text-sm transition-all duration-200 hover:scale-[1.03] active:scale-95 ${
            isDarkMode
              ? 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
          }`}
        >
          ← Back
        </button>
      ) : <span />}

      {currentStep < 3 ? (
        <button
          type="button"
          onClick={handleNext}
          className="btn-primary flex items-center gap-2 py-3 px-8 bg-gradient-to-r from-[#6c63ff] to-[#8b5cf6] hover:from-[#5b52ee] hover:to-[#7c3aed] text-white font-semibold text-sm rounded-2xl transition-all duration-200 hover:scale-[1.04] active:scale-95 shadow-lg hover:shadow-violet-500/30"
        >
          Continue →
        </button>
      ) : (
        <button
          type="submit"
          className="btn-primary flex items-center gap-2 py-3 px-8 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 text-white font-semibold text-sm rounded-2xl transition-all duration-200 hover:scale-[1.04] active:scale-95 shadow-lg hover:shadow-green-500/30"
        >
          🎉 Create Account
        </button>
      )}
    </div>
  );

  /* ── ROOT ── */
  return (
    <>
      <style>{STYLES}</style>

      <div
        className={`sgn-root flex flex-col min-h-screen transition-colors duration-500 overflow-x-hidden ${
          isDarkMode
            ? 'bg-gradient-to-br from-[#0a0a1a] via-[#0f0f24] to-[#14102a]'
            : 'bg-gradient-to-br from-[#f0f0fb] via-[#f8f8ff] to-[#eeeef8]'
        }`}
      >
        {/* animated background orbs */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
          <div className={`orb orb-1 ${isDarkMode ? 'orb-dk-1' : ''}`} />
          <div className={`orb orb-2 ${isDarkMode ? 'orb-dk-2' : ''}`} />
          <div className={`orb orb-3 ${isDarkMode ? 'orb-dk-3' : ''}`} />
        </div>

        <div className="relative" style={{ zIndex: 10 }}>
          <Header isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />
          <Navigation isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />
        </div>

        <div className="flex-grow flex justify-center items-start py-10 px-4 pt-[150px] pb-12" style={{ zIndex: 5, position: 'relative' }}>
          <div className="card-animate w-full max-w-2xl">

            {/* Glassmorphic card */}
            <div
              className={`relative rounded-3xl overflow-hidden shadow-2xl border transition-colors duration-500 ${
                isDarkMode
                  ? 'bg-[#12122a]/80 border-white/10 backdrop-blur-2xl'
                  : 'bg-white/75 border-white/60 backdrop-blur-xl shadow-violet-200/40'
              }`}
              style={{ boxShadow: isDarkMode
                ? '0 32px 80px rgba(0,0,0,.5), inset 0 1px 0 rgba(255,255,255,.06)'
                : '0 24px 70px rgba(108,99,255,.12), inset 0 1px 0 rgba(255,255,255,.9)' }}
            >
              {/* top accent strip */}
              <div className="h-1 w-full bg-gradient-to-r from-violet-500 via-indigo-400 to-blue-400" />

              <div className="p-8 md:p-10">
                {/* header */}
                <div className="text-center mb-8">
                  <h2
                    className={`text-3xl md:text-4xl font-800 tracking-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                    style={{ fontFamily: 'Sora, sans-serif', fontWeight: 800 }}
                  >
                    Create Account
                  </h2>
                  <p className={`mt-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Join your agency's workspace — takes under 2 minutes
                  </p>
                </div>

                {renderProgress()}

                {/* Error */}
                {errorMessage && (
                  <div
                    ref={errorRef}
                    className={`${shakeError ? 'shake' : ''} flex items-center gap-3 mb-5 px-4 py-3 rounded-xl text-sm font-medium bg-red-50 border border-red-200 text-red-700`}
                  >
                    <span className="text-base">⚠️</span> {errorMessage}
                  </div>
                )}

                <form onSubmit={handleSubmit} noValidate>
                  {currentStep === 1 && renderLoginDetails()}
                  {currentStep === 2 && renderUserDetails()}
                  {currentStep === 3 && renderAgencyDetails()}

                  {renderButtons()}

                  {/* sign in link */}
                  <p className={`text-center mt-6 text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    Already have an account?{' '}
                    <Link
                      to="/signin"
                      className={`font-semibold hover:underline ${isDarkMode ? 'text-violet-400 hover:text-violet-300' : 'text-violet-600 hover:text-violet-700'}`}
                    >
                      Sign In →
                    </Link>
                  </p>
                </form>
              </div>
            </div>

            {/* bottom decoration */}
            <p className={`text-center text-xs mt-5 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
              🔒 Your data is encrypted and secure
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Signup;